import React from 'react';
import type { ChipColor } from '@/types/chip';
import { PersonAdd, Assignment, Fingerprint, Security, HowToReg, HourglassEmpty } from '@mui/icons-material';

export interface OnboardingStep {
  stepId: string;
  stepName: string;
  status: string;
  completedAt?: string;
  [key: string]: unknown;
}

export interface OnboardingRecord {
  id?: string;
  status?: string;
  processId?: string;
  employee?: { id?: string };
  department?: string;
  targetStartDate?: string;
  initiatedAt?: string;
  completedAt?: string;
  steps?: OnboardingStep[];
  [key: string]: unknown;
}

export interface AlertRecord {
  id?: string;
  status?: string;
  alertType?: string;
  triggerDate?: string;
  alertId?: string;
  employee?: { id?: string };
  actionRequired?: string;
  actionTakenAt?: string;
  actionTakenBy?: string;
  alertSentAt?: string;
  [key: string]: unknown;
}

export interface InfoField {
  label: string;
  value: unknown;
  bold?: boolean;
  chip?: boolean;
  warning?: boolean;
}

export const ONBOARDING_STATUS = ['InProgress', 'Completed', 'Cancelled'] as const;
export const CONTRACT_ALERT_TYPES = ['ProbationExpiry', 'FixedTermExpiry'] as const;
export const CONTRACT_ALERT_STATUS = ['Pending', 'Triggered', 'Actioned', 'Dismissed'] as const;
export const DOCUMENT_TYPES = ['RSAID', 'Passport', 'TaxNumber', 'BankDetails', 'WorkPermit'] as const;
export const CONTRACT_TYPES = ['Permanent', 'FixedTerm', 'PartTime', 'Seasonal', 'IndependentContractor'] as const;
export const CONTRACT_STATUS = ['Active', 'Expired', 'Terminated', 'Renewed'] as const;
export const DOCUMENT_STATUS = ['Pending', 'Verified', 'Rejected'] as const;

export const STEP_ICONS: Record<string, React.ElementType> = {
  'personal-details': PersonAdd,
  'statutory-documents': Assignment,
  'contract-signing': Fingerprint,
  'popia-consent': Security,
  'system-provisioning': HowToReg,
  'induction': HourglassEmpty,
};

export const STATUS_COLORS: Record<string, ChipColor> = {
  InProgress: 'info',
  Completed: 'success',
  Cancelled: 'error',
  Pending: 'warning',
  Triggered: 'error',
  Actioned: 'success',
  Dismissed: 'default',
};

export const ALERT_TYPE_COLORS: Record<string, ChipColor> = {
  ProbationExpiry: 'warning',
  FixedTermExpiry: 'info',
};

export const DEFAULT_STEPS = [
  { stepId: 'personal-details', stepName: 'Personal Details Collection', status: 'Pending' },
  { stepId: 'statutory-documents', stepName: 'Statutory Document Upload', status: 'Pending' },
  { stepId: 'contract-signing', stepName: 'Employment Contract Signing', status: 'Pending' },
  { stepId: 'popia-consent', stepName: 'POPIA Consent', status: 'Pending' },
  { stepId: 'system-provisioning', stepName: 'System Access Provisioning', status: 'Pending' },
  { stepId: 'induction', stepName: 'HR Induction', status: 'Pending' },
];

export const getStepIcon = (stepId: string) => {
  const Icon = STEP_ICONS[stepId] || HourglassEmpty;
  return <Icon fontSize="small" />;
};
